import { type Dispatch } from '@reduxjs/toolkit';
import { type Stripe as StripeType } from '@stripe/stripe-js';
import { sendAnalyticsEvents } from '../../analytics';
import {
  EVENTS,
  PAYMENT_GATEWAY,
  PAYMENT_GATEWAY_PROPERTY,
  STRIPE_ACCOUNT_FALLBACK,
} from '../../core/constants';
import { setRequestResult, setShowPaymentError } from '../../core/store/checkout.slice';
import { setEventData } from '../../core/store/events';
import { setShowLoader } from '../../core/store/loader';
import { getPollingID, subscribeToPriceChange } from '../../core/store/checkoutFetch';
import { isValidPixHolderName, normalizeBrazilTaxIdForStripe, pixEmail } from './brazilTaxId';

type GetPollingIDWithPaymentMethod = (
  nonce: string | undefined,
  plan: string,
  token: string,
  analyticsParams: any,
  eventsData: any,
  onErrorHandler: (err: any) => void,
  payment_intent_id?: string,
  coupon?: string,
  email?: string,
  payment_method?: string | null,
) => Promise<string | Record<string, unknown> | undefined>;

export interface PixPurchaseProps {
  stripe: StripeType;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  successPayment?: (id1: string, id2: string, pltv?: number | null) => void;
  failedPayment?: () => void;
  coupon?: string;
  email?: string;
  returnUrl: string;
  billingDetails?: { name?: string; email?: string; tax_id?: string };
  stripePublishableKey: string;
}

export type CompletePixReturnPending = {
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  coupon?: string;
  email?: string;
};

export const pixPurchase =
  ({
    stripe,
    plan,
    token,
    analyticsParams,
    eventsData,
    successPayment,
    failedPayment,
    coupon,
    email = '',
    returnUrl,
    billingDetails,
    stripePublishableKey,
  }: PixPurchaseProps) =>
  async (dispatch: Dispatch) => {
    const userEmail = pixEmail(email, billingDetails, eventsData, analyticsParams);

    const onError = (data: any, errorData?: any) => {
      failedPayment?.();
      let txt = data;
      if (typeof txt !== 'string') txt = data?.message;
      if (typeof txt !== 'string') txt = 'Payment error';
      dispatch(
        setEventData({
          declineCode: errorData?.decline_code || errorData?.processor_response,
        }),
      );
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError(txt));
      dispatch(setShowLoader(false));
    };
    const onSuccess = (
      sub_id: string,
      event_id: string,
      paymentData?: { customer_id?: string; pltv?: number | null },
    ) => {
      successPayment?.(sub_id, event_id, paymentData?.pltv);
      dispatch(setRequestResult({ isSuccess: true, id: sub_id }));
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, {
        ...eventsData,
        ...(paymentData?.customer_id != null && paymentData.customer_id !== ''
          ? { customer_id: paymentData.customer_id }
          : {}),
      });
      dispatch(setShowLoader(false));
    };

    dispatch(setShowLoader(true));
    sendAnalyticsEvents(EVENTS.PAYMENT_ANIMATION, eventsData);

    try {
      const pixResponse = await (getPollingID as GetPollingIDWithPaymentMethod)(
        '',
        plan,
        token,
        analyticsParams,
        eventsData,
        (err: any) => onError(err?.message || err),
        '',
        coupon,
        userEmail,
        'pix_automatico',
      );

      if (!pixResponse || typeof pixResponse !== 'object') {
        dispatch(setShowLoader(false));
        return;
      }

      let paymentIntentSecret = (pixResponse as { payment_intent_secret?: string })
        .payment_intent_secret;
      let pollingId = (pixResponse as { polling_result_id?: string }).polling_result_id;

      if (!paymentIntentSecret && pollingId) {
        const pollResult = await subscribeToPriceChange(pollingId);
        const status = pollResult?.status;
        const data = pollResult?.data;
        if (status === 'requires_action' && data?.payment_intent_secret) {
          paymentIntentSecret = data.payment_intent_secret;
        } else if (status && pollResult?.subscription_id) {
          onSuccess(pollResult.subscription_id, pollResult.event_id, {
            customer_id: pollResult.customer_id,
            pltv: pollResult.pltv,
          });
          return;
        } else if (pollResult?.errors) {
          onError(pollResult.errors?.messages || 'An error has occurred', data);
          return;
        }
      }

      if (!paymentIntentSecret) {
        onError('Could not start PIX payment', undefined);
        return;
      }

      const stripeAny = stripe as any;
      const confirmPix = stripeAny.confirmPixPayment;
      if (typeof confirmPix !== 'function') {
        onError('PIX payment is not supported', undefined);
        return;
      }

      const taxIdNorm = normalizeBrazilTaxIdForStripe(billingDetails?.tax_id || '');
      if (!taxIdNorm) {
        onError('Please enter a valid CPF or CNPJ for PIX.', { code: 'tax_id_invalid' });
        return;
      }
      const holderNameTrimmed = (billingDetails?.name || '').trim();
      if (!isValidPixHolderName(holderNameTrimmed)) {
        onError('Please enter a valid name for PIX.', {
          code: 'pix_name_invalid',
        });
        return;
      }

      localStorage.setItem(
        'pix_pending',
        JSON.stringify({
          plan,
          token,
          analyticsParams,
          eventsData,
          coupon,
          email: userEmail,
          stripePublishableKey,
          tax_id: taxIdNorm,
          holder_name: holderNameTrimmed,
        }),
      );

      const confirmParams: {
        return_url: string;
        payment_method: {
          billing_details: { name: string; email?: string; tax_id: string };
        };
      } = {
        return_url: returnUrl,
        payment_method: {
          billing_details: {
            name: holderNameTrimmed,
            ...(userEmail && { email: userEmail }),
            tax_id: taxIdNorm,
          },
        },
      };

      const result = await confirmPix(paymentIntentSecret, confirmParams);

      const err = result?.error;
      const paymentIntent = result?.paymentIntent;

      if (err) {
        localStorage.removeItem('pix_pending');
        onError(err.message, err);
        return;
      }

      if (paymentIntent?.id) {
        localStorage.removeItem('pix_pending');
        const secondResponse = await (getPollingID as GetPollingIDWithPaymentMethod)(
          undefined,
          plan,
          token,
          analyticsParams,
          eventsData,
          (e: any) => onError(e?.message || e),
          paymentIntent.id,
          coupon,
          userEmail,
          'pix_automatico',
        );

        if (
          secondResponse &&
          typeof secondResponse === 'object' &&
          (secondResponse as { polling_result_id?: string }).polling_result_id
        ) {
          const pollingResultId = (
            secondResponse as {
              polling_result_id: string;
            }
          ).polling_result_id;
          const finalResult = await subscribeToPriceChange(pollingResultId);
          if (finalResult?.status && finalResult.subscription_id) {
            onSuccess(finalResult.subscription_id, finalResult.event_id, {
              customer_id: finalResult.customer_id,
              pltv: finalResult.pltv,
            });
            return;
          }
          if (finalResult?.errors) {
            onError(finalResult.errors?.messages || 'Payment failed', finalResult?.data);
          }
        } else {
          onError('Could not complete payment', undefined);
        }
      }
    } catch (err: any) {
      localStorage.removeItem('pix_pending');
      onError(err?.message || 'Payment error', undefined);
    } finally {
      dispatch(setShowLoader(false));
    }
  };

export const completePixReturn =
  ({
    stripe,
    clientSecret,
    pending,
    onSuccess,
    onError,
  }: {
    stripe: StripeType;
    clientSecret: string;
    pending: CompletePixReturnPending;
    onSuccess: (
      sub_id: string,
      event_id: string,
      paymentData?: { customer_id?: string; pltv?: number | null },
    ) => void;
    onError: (message: string) => void;
  }) =>
  async (dispatch: Dispatch) => {
    const { plan, token, analyticsParams, eventsData, coupon, email: storedEmail } = pending;
    const userEmail = pixEmail(storedEmail, undefined, eventsData, analyticsParams);
    dispatch(setShowLoader(true));
    try {
      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
      if (error) {
        onError(error.message || 'Could not retrieve payment');
        return;
      }
      if (!paymentIntent?.id) {
        onError('Invalid payment intent');
        return;
      }
      const secondResponse = await (getPollingID as GetPollingIDWithPaymentMethod)(
        undefined,
        plan,
        token,
        analyticsParams,
        eventsData,
        (e: any) => onError(e?.message || e),
        paymentIntent.id,
        coupon,
        userEmail,
        'pix_automatico',
      );
      if (
        !secondResponse ||
        typeof secondResponse !== 'object' ||
        !(secondResponse as { polling_result_id?: string }).polling_result_id
      ) {
        onError('Could not complete payment');
        return;
      }
      const pollingResultId = (
        secondResponse as {
          polling_result_id: string;
        }
      ).polling_result_id;
      const finalResult = await subscribeToPriceChange(pollingResultId);
      if (finalResult?.status && finalResult.subscription_id) {
        dispatch(
          setRequestResult({
            isSuccess: true,
            id: finalResult.subscription_id,
          }),
        );
        sendAnalyticsEvents(EVENTS.SUBSCRIBED, {
          ...eventsData,
          [PAYMENT_GATEWAY_PROPERTY]:
            eventsData?.[PAYMENT_GATEWAY_PROPERTY] ||
            PAYMENT_GATEWAY.stripe(eventsData?.stripe_account_name, STRIPE_ACCOUNT_FALLBACK.PIX),
          ...(finalResult.customer_id != null && finalResult.customer_id !== ''
            ? { customer_id: finalResult.customer_id }
            : {}),
        });
        onSuccess(finalResult.subscription_id, finalResult.event_id, {
          pltv: finalResult.pltv,
        });
      } else if (finalResult?.errors) {
        onError(finalResult.errors?.messages || 'Payment failed');
      }
    } catch (err: any) {
      onError(err?.message || 'Payment error');
    } finally {
      dispatch(setShowLoader(false));
    }
  };
